import * as Api from './sdk';
import type { UserSessionContext } from './viewModels/index';

export interface IStreamGrant {
	accountId: string;
	expiresAt: number;
	grant: string;
	/** The signed-in user the grant was minted under, which can differ from the user it authenticates as. */
	sessionUserId: string;
	userId: string;
}

/** Re-mint slightly ahead of expiry so a connect already in flight cannot present a dead grant. */
const ExpiryLeewayMs = 30000;

/**
 * Holds the event-stream grant for one tab. Every connection in the tab shares one grant: a broker
 * outage re-runs each connection's configure step every few seconds, so minting per connect would
 * turn one outage into a mint stampede.
 */
export class StreamGrantStore {
	private mUserSession: UserSessionContext;
	private mGrant: IStreamGrant;
	private mPendingMint: Promise<IStreamGrant>;
	private mPendingMintOwner: string;
	private mGeneration = 0;

	constructor(userSession: UserSessionContext) {
		this.mUserSession = userSession;
	}

	public get = async (): Promise<IStreamGrant> => {
		const accountId = this.mUserSession.account?.id;
		const sessionUserId = this.mUserSession.user?.id;
		if (!accountId || !sessionUserId) {
			return null;
		}

		if (this.isUsable(this.mGrant, accountId, sessionUserId)) {
			return this.mGrant;
		}
		this.mGrant = null;

		const owner = `${accountId}:${sessionUserId}`;
		if (this.mPendingMint && this.mPendingMintOwner !== owner) {
			this.clear();
		}

		if (!this.mPendingMint) {
			const pendingMint = this.mint(accountId, sessionUserId).finally(() => {
				if (this.mPendingMint === pendingMint) {
					this.mPendingMint = null;
				}
			});
			this.mPendingMint = pendingMint;
			this.mPendingMintOwner = owner;
		}
		return this.mPendingMint;
	};

	public clear = () => {
		this.mGeneration = this.mGeneration + 1;
		this.mGrant = null;
		this.mPendingMint = null;
		this.mPendingMintOwner = null;
	};

	private isUsable = (grant: IStreamGrant, accountId: string, sessionUserId: string) =>
		!!grant &&
		grant.accountId === accountId &&
		grant.sessionUserId === sessionUserId &&
		grant.expiresAt - ExpiryLeewayMs > Date.now();

	private mint = async (accountId: string, sessionUserId: string): Promise<IStreamGrant> => {
		const generation = this.mGeneration;
		const result = await this.mUserSession.webServiceHelper.callWebServiceAsync<Api.IStreamGrantResponse>(
			'user/streamGrant',
			'POST'
		);
		// A mint that was in flight when the session ended belongs to the user who just left.
		if (generation !== this.mGeneration || !result.success || !result.value?.grant) {
			return null;
		}

		this.mGrant = {
			accountId,
			expiresAt: new Date(result.value.expiresAt).getTime(),
			grant: result.value.grant,
			sessionUserId,
			userId: result.value.userId,
		};
		return this.mGrant;
	};
}

const stores = new WeakMap<UserSessionContext, StreamGrantStore>();

export const getStreamGrantStore = (userSession: UserSessionContext) => {
	let store = stores.get(userSession);
	if (!store) {
		store = new StreamGrantStore(userSession);
		stores.set(userSession, store);
	}
	return store;
};
